import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { type Capability } from '@/lib/auth/capabilities';
import { hasAnyInvestorPortalCapability } from '@/lib/auth/investorPortalAuthority';
import { useResolvedInvestorCapabilities } from '@/hooks/useResolvedInvestorCapabilities';
import { useAppearancePreferences } from '@/hooks/useAppearancePreferences';
import { resolveInvestorStartPage } from '@/lib/investor/startPage';

export const InvestorCapabilityGuard: React.FC<{
  requiredAny: readonly Capability[];
  children: React.ReactNode;
}> = ({ requiredAny, children }) => {
  const { user, loading } = useAuth();
  const capabilities = useResolvedInvestorCapabilities();
  if (loading) return <div className="min-h-screen bg-app" aria-busy="true" />;
  if (!requiredAny.some((capability) => capabilities.includes(capability))
    && !hasAnyInvestorPortalCapability(user, requiredAny)) {
    return <Navigate to="/unauthorized" replace />;
  }
  return <>{children}</>;
};

export const InvestorLandingRedirect: React.FC = () => {
  const { user, loading } = useAuth();
  const capabilities = useResolvedInvestorCapabilities();
  const { appearance } = useAppearancePreferences(user?.id ?? 'anonymous');
  if (loading) return <div className="min-h-screen bg-app" aria-busy="true" />;
  const destination = resolveInvestorStartPage(appearance.investorStartPage, capabilities);
  return <Navigate to={destination} replace />;
};
